import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  CartesianViewportHandle,
  FigureElement,
  FigureHandle,
  FormulaLegendHandle,
  ReadoutHandle,
  ReadoutRow,
} from '@learnforge/demo-render';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import { createCartesianViewport, createFormulaLegend, createSvgEl } from '@learnforge/demo-render';
import { parse, taylorCoefficients, taylorPolynomial, stringify } from '@learnforge/demo-math/expr';
import type { CalculusContext } from '../types';
import type { CalcKey } from '../locales/keys';
import { createCurveLayer, type CurveLayerHandle } from '../render/curve';
import { compileFn, sampleFn } from '../calc';
import { TAYLOR_PRESETS, type TaylorPreset } from '../data/taylor-presets';
import { createCartesianPlane, type CartesianPlaneHandle } from '../render/cartesian-plane';

// ─── Stage layout constants ───────────────────────────────────────────────────

/** Stage dimensions in pixels. */
const STAGE_W = 800;
const STAGE_H = 460;
const BASE_Y_DOMAIN: [number, number] = [-4.6, 4.6];
const PLOT_RECT = { x: 0, y: 0, width: STAGE_W, height: STAGE_H };
/** Number of sample points for the function curve. */
const SAMPLE_N = 400;
/** Maximum allowed degree for the Taylor polynomial. */
const MAX_DEGREE = 12;
const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

function subscriptNumber(value: number): string {
  return String(value)
    .split('')
    .map((digit) => SUBSCRIPT_DIGITS[Number(digit)] ?? digit)
    .join('');
}

// ─── State ────────────────────────────────────────────────────────────────────

export interface TaylorLabState {
  presetId: string;
  degree: number;
  center: number;
}

// ─── Pure helpers (exported and tested) ──────────────────────────────────────

/**
 * Compute the Taylor polynomial model for the given state.
 *
 * Returns:
 *  - `polySource`: the exact Pₙ(x) as a stringified expression
 *  - `coeffs`: Taylor coefficients a_k = f^(k)(center) / k! for k = 0..degree
 */
export function taylorModel(state: TaylorLabState): { polySource: string; coeffs: number[] } {
  const preset = TAYLOR_PRESETS.find((p) => p.id === state.presetId) ?? TAYLOR_PRESETS[0]!;
  const degree = Math.min(state.degree, MAX_DEGREE);
  const fNode = parse(preset.source).node;

  const coeffs = taylorCoefficients(fNode, 'x', state.center, degree);
  const polyNode = taylorPolynomial(fNode, 'x', state.center, degree);
  const polySource = stringify(polyNode);

  return { polySource, coeffs };
}

// ─── Spec factory ─────────────────────────────────────────────────────────────

function buildSpec(): ExperimentSpec<TaylorLabState, CalcKey, CalculusContext> {
  // Closure-stashed handles created in setup():
  let plane: CartesianPlaneHandle;
  let figure: FigureHandle;
  let fCurves: CurveLayerHandle;
  let polyCurves: CurveLayerHandle;
  let readout: ReadoutHandle;
  let viewport: CartesianViewportHandle;
  let legend: FormulaLegendHandle;
  let activePresetId = TAYLOR_PRESETS[0]!.id;
  // Managed SVG <g> for the convergence-band shading; recreated each render.
  let bandGroup: SVGGElement | null = null;

  function defaultState(_ctx: CalculusContext): TaylorLabState {
    const preset = TAYLOR_PRESETS[0]!;
    return {
      presetId: preset.id,
      degree: 4,
      center: preset.center,
    };
  }

  function applyPresetLocal(state: TaylorLabState, id: string): TaylorLabState {
    const preset = TAYLOR_PRESETS.find((p) => p.id === id);
    if (!preset) return state;
    return { ...state, presetId: preset.id, center: preset.center };
  }

  function getPreset(state: TaylorLabState): TaylorPreset {
    return TAYLOR_PRESETS.find((p) => p.id === state.presetId) ?? TAYLOR_PRESETS[0]!;
  }

  function clearBandGroup(): void {
    if (bandGroup) {
      bandGroup.remove();
      bandGroup = null;
    }
  }

  return {
    labelKey: 'experiment.taylor-lab',
    demoId: 'calculus',
    advancedKey: 'taylor-lab',

    initialState(ctx): TaylorLabState {
      return defaultState(ctx);
    },

    applyPreset(state, id): TaylorLabState {
      return applyPresetLocal(state, id);
    },

    setup(ctx, controller: HarnessController<TaylorLabState>): void {
      const base = { xDomain: TAYLOR_PRESETS[0]!.domain, yDomain: BASE_Y_DOMAIN };
      plane = createCartesianPlane(controller.svg, { domains: base, plotRect: PLOT_RECT });
      controller.registerCleanup(() => plane.destroy());
      // f curve layer (bold, back)
      fCurves = createCurveLayer(plane.content, plane);
      controller.registerCleanup(() => fCurves.destroy());
      // Pₙ curve layer (light, front of f but behind figure)
      polyCurves = createCurveLayer(plane.content, plane);
      controller.registerCleanup(() => polyCurves.destroy());
      figure = controller.createFigure();
      readout = controller.createReadout();
      legend = createFormulaLegend(ctx.root);
      controller.registerCleanup(() => legend.destroy());
      viewport = createCartesianViewport(controller.svg, {
        base,
        plotRect: PLOT_RECT,
        onChange(next) {
          plane.setDomains(next);
          controller.requestRender();
        },
      });
      controller.registerCleanup(() => viewport.dispose());
      // Band group cleanup on dispose
      controller.registerCleanup(() => clearBandGroup());
    },

    render(state: TaylorLabState, ctx): void {
      const preset = getPreset(state);
      if (preset.id !== activePresetId) {
        const base = { xDomain: preset.domain, yDomain: BASE_Y_DOMAIN };
        activePresetId = preset.id;
        viewport.setBase(base, { notify: false });
        plane.setDomains(base);
      }
      const degree = Math.min(state.degree, MAX_DEGREE);
      const center = state.center;

      const domains = plane.getDomains();
      const [domainLo, domainHi] = domains.xDomain;

      // ── Compile f ──
      const f = compileFn(preset.source);

      // ── Draw f (bold primary curve) ──
      const fPts = sampleFn(f, domainLo, domainHi, SAMPLE_N);
      fCurves.setPolyline('f', fPts, 'calc-curve-primary');

      // ── Compute Taylor polynomial ──
      const { polySource, coeffs } = taylorModel({ presetId: state.presetId, degree, center });
      const poly = compileFn(polySource);

      // ── Draw Pₙ (light secondary curve) ──
      // Intentionally drawn over the full domain so divergence outside the
      // interval of convergence is visible (pedagogically desired).
      // The curve layer breaks the path at non-finite y automatically.
      const polyPts = sampleFn(poly, domainLo, domainHi, SAMPLE_N);
      polyCurves.setPolyline('poly', polyPts, 'calc-curve-secondary');

      // ── Convergence band shading ──
      clearBandGroup();
      const r = preset.convergenceRadius;
      if (r !== null && Number.isFinite(r) && r > 0) {
        const bandLo = center - r;
        const bandHi = center + r;

        // Project the band corners to screen coords
        const screenLeft = plane.worldToScreen({ x: bandLo, y: 0 });
        const screenRight = plane.worldToScreen({ x: bandHi, y: 0 });

        // The band is a full-height vertical strip
        bandGroup = createSvgEl('g', { class: 'calc-convergence-band' });
        const bandRect = createSvgEl('rect', {
          x: String(Math.min(screenLeft.x, screenRight.x)),
          y: String(0),
          width: String(Math.abs(screenRight.x - screenLeft.x)),
          height: String(STAGE_H),
          class: 'calc-area',
        });
        bandGroup.appendChild(bandRect);

        // Left boundary line
        const leftLine = createSvgEl('line', {
          x1: String(screenLeft.x),
          y1: String(0),
          x2: String(screenLeft.x),
          y2: String(STAGE_H),
          class: 'calc-curve-faint',
          'stroke-dasharray': '6 4',
        });
        bandGroup.appendChild(leftLine);

        // Right boundary line
        const rightLine = createSvgEl('line', {
          x1: String(screenRight.x),
          y1: String(0),
          x2: String(screenRight.x),
          y2: String(STAGE_H),
          class: 'calc-curve-faint',
          'stroke-dasharray': '6 4',
        });
        bandGroup.appendChild(rightLine);

        // Insert the band before the f-curve layer so it renders behind the curves.
        plane.content.insertBefore(bandGroup, fCurves.group);
      }

      // ── Figure: draggable center point on x-axis ──
      const screenCenter = plane.worldToScreen({ x: center, y: 0 });

      const elements: FigureElement[] = [
        {
          kind: 'point',
          id: 'pt-center',
          at: screenCenter,
          style: { className: 'geo-point-draggable' },
          draggable: {},
        },
      ];

      figure.update(elements);
      fCurves.group.ownerSVGElement
        ?.querySelector('[data-figure-id="pt-center"]')
        ?.setAttribute('data-no-pan', 'true');
      legend.setEntries([
        { id: 'f', formula: `f(x) = ${preset.source}`, color: 'var(--accent)' },
        {
          id: 'polynomial',
          formula: `P${subscriptNumber(degree)}(x) = ${polySource}`,
          color: 'var(--ink-soft)',
        },
      ]);

      // ── Readout ──
      const rows: ReadoutRow[] = [];

      // Polynomial expression row
      rows.push({
        id: 'poly-expr',
        lhs: 'Pₙ(x)',
        relation: '=',
        rhs: polySource,
      });

      // Coefficient rows
      const fmtCoeff = (v: number): string => {
        if (!Number.isFinite(v)) return '?';
        return Math.abs(v) < 0.001 ? v.toExponential(3) : v.toFixed(4);
      };

      for (let k = 0; k <= degree; k++) {
        rows.push({
          id: `coeff-${k}`,
          lhs: `a${k}`,
          relation: '=',
          rhs: fmtCoeff(coeffs[k] ?? 0),
        });
      }

      // Advanced: Lagrange remainder bound row
      if (ctx.advanced) {
        // |Rₙ(x)| ≤ M/(n+1)! · |x - c|^(n+1) where M = max |f^(n+1)| on domain.
        // We approximate M numerically by evaluating the (n+1)-th derivative at
        // several sample points over the domain and taking the max |value|.
        // For display purposes, we show the bound at x = center ± convergenceRadius
        // (or ± 1 for infinite-radius presets).
        const evalRadius = r !== null ? Math.min(r, 1) : 1;
        const xSample = center + evalRadius;
        // Compute (n+1)-th derivative numerically via finite difference of the n-th Taylor coeff
        // A simpler pedagogical bound: |R_n| ≈ |a_{n+1}| * |x - c|^{n+1} where a_{n+1} is
        // estimated via one more degree. Use degree+1 coefficient if feasible.
        try {
          const higherCoeffs = taylorCoefficients(
            parse(preset.source).node,
            'x',
            center,
            degree + 1,
          );
          const aNplus1 = higherCoeffs[degree + 1] ?? 0;
          const bound = Math.abs(aNplus1) * Math.pow(Math.abs(xSample - center), degree + 1);
          rows.push({
            id: 'lagrange-bound',
            lhs: `|R${degree}(${evalRadius > 0 ? '+' : ''}${evalRadius.toFixed(1)})|`,
            relation: '≤',
            rhs: Number.isFinite(bound) ? bound.toExponential(3) : '∞',
            emphasis: 'neutral',
          });
        } catch {
          // If the higher derivative fails (e.g. singularity), skip the row.
        }
      }

      readout.update(rows);
    },

    onDrag(state: TaylorLabState, pointId: string, at: Vec2): TaylorLabState {
      if (pointId === 'pt-center') {
        const mathX = plane.screenToWorld(at).x;
        // Clamp center to the preset domain
        const preset = getPreset(state);
        const [lo, hi] = preset.domain;
        const clampedCenter = Math.max(lo, Math.min(hi, mathX));
        return { ...state, center: clampedCenter };
      }
      return state;
    },

    panel(state: TaylorLabState, _ctx): PanelRow<CalcKey, TaylorLabState>[] {
      const rows: PanelRow<CalcKey, TaylorLabState>[] = [];
      const preset = getPreset(state);
      const [domainLo, domainHi] = preset.domain;

      // Preset dropdown
      rows.push({
        kind: 'dropdown',
        id: 'taylor-preset',
        labelKey: 'taylor.panel.preset',
        options: TAYLOR_PRESETS.map((p) => ({ value: p.id, labelKey: p.labelKey })),
        value: state.presetId,
        onChange: (v) => applyPresetLocal(state, v),
      });

      // Degree slider (each control has its own label key — not 'panel.advanced')
      rows.push({
        kind: 'slider',
        id: 'taylor-degree',
        labelKey: 'taylor.panel.degree',
        min: 0,
        max: MAX_DEGREE,
        step: 1,
        value: state.degree,
        format: (v) => String(Math.round(v)),
        onChange: (v) => ({ ...state, degree: Math.round(v) }),
      });

      // Center slider (its own label key)
      rows.push({
        kind: 'slider',
        id: 'taylor-center',
        labelKey: 'taylor.panel.center',
        min: domainLo,
        max: domainHi,
        step: 0.1,
        value: state.center,
        format: (v) => v.toFixed(1),
        onChange: (v) => ({ ...state, center: v }),
      });

      // Advanced toggle
      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      return rows;
    },

    actions: (_state: TaylorLabState, ctx): ActionBarSpec<CalcKey, TaylorLabState> => {
      return {
        onReset: () => defaultState(ctx),
        onRandomise: (s) => {
          const idx = Math.floor(ctx.prng() * TAYLOR_PRESETS.length);
          const preset = TAYLOR_PRESETS[idx]!;
          return { ...s, presetId: preset.id, center: preset.center };
        },
        presets: TAYLOR_PRESETS.map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id),
        viewReset: { labelKey: 'action.reset-view', onResetView: () => viewport.reset() },
      };
    },

    applyShotState(state: TaylorLabState, _shotId: string): TaylorLabState {
      return { ...state, degree: 5, center: 0 };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
